import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  HOME_HERO_IMAGES,
  HOME_HERO_SLIDE_INTERVAL_MS,
  HOME_HERO_TRANSITION_MS,
} from '../../constants/homeHeroImages';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

interface HeroImageCarouselProps {
  images?: readonly string[];
  intervalMs?: number;
  transitionMs?: number;
  className?: string;
  ariaLabel?: string;
}

const PREFETCH_LEAD_MS = 400;

const HeroImageCarousel: React.FC<HeroImageCarouselProps> = ({
  images = HOME_HERO_IMAGES,
  intervalMs = HOME_HERO_SLIDE_INTERVAL_MS,
  transitionMs = HOME_HERO_TRANSITION_MS,
  className,
  ariaLabel = 'Hero images',
}) => {
  const reduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentReady, setCurrentReady] = useState(false);

  const imagesKey = useMemo(() => images.join('|'), [images]);

  useEffect(() => {
    setActiveIndex(0);
    setCurrentReady(false);
  }, [imagesKey]);

  const preload = useCallback((src: string, onReady?: () => void) => {
    const img = new Image();
    img.onload = () => {
      onReady?.();
    };
    img.src = src;
  }, []);

  useEffect(() => {
    const src = images[activeIndex];
    if (!src) {
      return undefined;
    }
    let cancelled = false;
    setCurrentReady(false);
    preload(src, () => {
      if (!cancelled) {
        setCurrentReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeIndex, images, preload]);

  useEffect(() => {
    if (reduceMotion || images.length <= 1) {
      return undefined;
    }
    const nextIndex = (activeIndex + 1) % images.length;
    const nextSrc = images[nextIndex];
    const delay = Math.max(0, intervalMs - PREFETCH_LEAD_MS);
    const timer = window.setTimeout(() => {
      preload(nextSrc);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [activeIndex, images, intervalMs, preload, reduceMotion]);

  useEffect(() => {
    if (reduceMotion || images.length <= 1) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [images.length, intervalMs, reduceMotion]);

  const currentSrc = currentReady
    ? (images[activeIndex] ?? FALLBACK_IMAGE)
    : FALLBACK_IMAGE;
  const transitionSeconds = transitionMs / 1000;

  return (
    <CarouselRoot
      className={className}
      aria-live="off"
      aria-label={ariaLabel}
    >
      <AnimatePresence mode="sync" initial={false}>
        <Slide
          key={currentSrc}
          src={currentSrc}
          alt=""
          role="presentation"
          loading="eager"
          fetchPriority="high"
          draggable={false}
          onContextMenu={(event) => event.preventDefault()}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: transitionSeconds, ease: 'easeInOut' }}
        />
      </AnimatePresence>
    </CarouselRoot>
  );
};

const CarouselRoot = styled.div`
  position: absolute;
  inset: 0;
`;

const Slide = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export default HeroImageCarousel;
